import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, DatePicker, Descriptions, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Select, Table } from 'antd';
import { listRoomReports, createRoomReport, updateRoomReport, deleteRoomReport, applyHoldRoomReport, finalizeRoomReport, createMoveOutRoomReport } from '../../api/roomReports';
import { listStudents } from '../../api/students';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import { getApiErrorMessage } from '../../utils/apiError';
import { isPageVisible } from '../../utils/pageVisibility';

const CONDITION_ITEMS = [
  'Walls/paint/ceiling',
  'Desk/Chair/Bed/Mattress',
  'Windows/Screens/Curtain',
  'Closets',
  'Doors/Locks',
  'Fans',
  'Lights/Socket',
  'Floor',
  'Key',
  'Plumbing',
];

const CONDITION_CODES = [
  { label: 'NCC (needs complete cleaning)', value: 'NCC' },
  { label: 'REP (replace)', value: 'REP' },
  { label: 'SP (needs spots painting)', value: 'SP' },
  { label: 'RPR (needs repair)', value: 'RPR' },
  { label: 'PT (needs painting)', value: 'PT' },
  { label: 'SCR (scratched)', value: 'SCR' },
  { label: 'CLN (clean)', value: 'CLN' },
  { label: 'NEW (new)', value: 'NEW' },
];

function parseEquipementsText(text) {
  if (!text) return [];
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length)
    .map((line) => {
      // Format attendu: "Label: CODE - commentaire" (mais on reste tolérant)
      const [left, commentPart] = line.split(' - ');
      const [labelPart, codePart] = left.split(':');
      return {
        label: (labelPart || '').trim(),
        code: (codePart || '').trim(),
        comment: (commentPart || '').trim(),
        raw: line,
      };
    });
}

function getConditionRows(report) {
  const items = report?.RoomReportItems;
  if (Array.isArray(items) && items.length) {
    return items.map((it, idx) => ({
      label: it?.label || '',
      code: it?.code || '',
      comment: it?.comment || '',
      raw: String(it?.id ?? idx),
    }));
  }
  return parseEquipementsText(report?.equipements);
}

function renderStatusTag(status) {
  const v = String(status || 'DRAFT');
  const label = v === 'SIGNED_BY_STUDENT' ? 'SIGNED' : v;
  const cls = v === 'FINAL' ? 'tag-green'
    : v === 'HOLD' ? 'tag-red'
    : v === 'SIGNED_BY_STUDENT' ? 'tag-blue'
    : v === 'DRAFT' ? 'tag-amber'
    : 'tag-grey';
  return <span className={`tag ${cls}`}>{label}</span>;
}

export default function Reports() {
  const { message, notification } = App.useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [studentById, setStudentById] = useState({});
  const [students, setStudents] = useState([]);
  const [filters, setFilters] = useState({ valide: '' });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdTarget, setHoldTarget] = useState(null);
  const [moveOutOpen, setMoveOutOpen] = useState(false);
  const [moveOutTarget, setMoveOutTarget] = useState(null);
  const [pickStudentOpen, setPickStudentOpen] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [form] = Form.useForm();
  const [holdForm] = Form.useForm();
  const [moveOutForm] = Form.useForm();

  const inspectionType = Form.useWatch('inspection_type', form);
  const selectedStudentFormId = Form.useWatch('student_id', form);

  useEffect(() => {
    if (!selectedStudentFormId) return;
    if (editing?.id) return;

    const current = form.getFieldValue('room_number');
    if (current) return;

    const s = studentById?.[selectedStudentFormId];
    const roomCode = s?.RoomAssignments?.[0]?.Room?.code || s?.chambre || '';
    if (!roomCode) return;

    form.setFieldsValue({ room_number: roomCode });
  }, [editing?.id, form, selectedStudentFormId, studentById]);

  const blockedMoveInStudentIds = useMemo(() => {
    const set = new Set();
    const list = Array.isArray(rows) ? rows : [];
    list.forEach((r) => {
      if (r?.inspection_type !== 'MOVE_IN') return;
      if (!r?.student_signed_at) return;
      if (r?.student_rejected_at) return;
      const children = Array.isArray(r?.Children) ? r.Children : [];
      const hasFinalMoveOut = children.some((c) => c && c.inspection_type === 'MOVE_OUT' && c.status === 'FINAL');
      if (!hasFinalMoveOut && r?.student_id) {
        set.add(String(r.student_id));
      }
    });
    return set;
  }, [rows]);

  const isSelectedStudentMoveInBlocked = useMemo(() => {
    if (inspectionType !== 'MOVE_IN') return false;
    if (!selectedStudentFormId) return false;
    return blockedMoveInStudentIds.has(String(selectedStudentFormId));
  }, [blockedMoveInStudentIds, inspectionType, selectedStudentFormId]);

  const editingHasFinalMoveOut = useMemo(() => {
    if (!editing || editing?.inspection_type !== 'MOVE_IN') return false;
    const children = Array.isArray(editing?.Children) ? editing.Children : [];
    return children.some((c) => c && c.inspection_type === 'MOVE_OUT' && c.status === 'FINAL');
  }, [editing]);

  const isSaveDraftDisabled = useMemo(() => {
    if (!editing) return false;
    return editing?.status === 'FINAL' || !!editing?.student_signed_at;
  }, [editing]);

  const fetchAll = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) setLoading(true);
    try {
      const params = {};
      if (filters.valide) params.valide = filters.valide;
      const [reports, studs] = await Promise.all([
        listRoomReports(params),
        listStudents({}),
      ]);
      const nextRows = Array.isArray(reports) ? reports : [];
      setRows(nextRows);
      setViewing((v) => {
        if (!v?.id) return v;
        return nextRows.find((r) => r?.id === v.id) || v;
      });
      setEditing((e) => {
        if (!e?.id) return e;
        return nextRows.find((r) => r?.id === e.id) || e;
      });
      const safeStuds = Array.isArray(studs) ? studs : [];
      setStudents(safeStuds);
      const index = Object.fromEntries(safeStuds.map((s) => [s.id, s]));
      setStudentById(index);
    } catch (e) {
      if (!silent) {
        notification.error({
          message: getApiErrorMessage(e, 'Impossible de charger les rapports de chambre.'),
          placement: 'topRight',
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filters.valide, notification]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPageVisible()) fetchAll({ silent: true });
    }, 10000);
    const onVisibility = () => {
      if (isPageVisible()) fetchAll({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchAll]);

  useEffect(() => {
    const onRefresh = () => {
      fetchAll();
    };
    window.addEventListener('reports:refresh', onRefresh);
    return () => window.removeEventListener('reports:refresh', onRefresh);
  }, [fetchAll]);

  const onAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ inspection_type: 'MOVE_IN', items: CONDITION_ITEMS.map((label) => ({ label })) });
    setSelectedStudentId(null);
    setStudentSearch('');
    setOpen(true);
  };
  const onStudentChange = () => {
    form.setFieldsValue({});
  };
  const openPickStudent = () => {
    setPickStudentOpen(true);
    setStudentSearch('');
    const current = form.getFieldValue('student_id') || null;
    setSelectedStudentId(current);
  };

  const studentRows = useMemo(() => {
    const q = String(studentSearch || '').trim().toLowerCase();
    const base = Array.isArray(students) ? students : [];
    if (!q) return base;
    return base.filter((s) => {
      const code = String(s?.student_code || '').toLowerCase();
      const prenom = String(s?.User?.prenom || '').toLowerCase();
      const nom = String(s?.User?.nom || '').toLowerCase();
      const email = String(s?.User?.email || '').toLowerCase();
      const residence = String(s?.Residence?.nom || '').toLowerCase();
      return code.includes(q) || prenom.includes(q) || nom.includes(q) || email.includes(q) || residence.includes(q);
    });
  }, [studentSearch, students]);

  const studentColumns = useMemo(() => [
    { title: 'ID', dataIndex: 'student_code', width: 120 },
    { title: 'Nom', render: (_, s) => `${s?.User?.prenom || ''} ${s?.User?.nom || ''}`.trim() },
    { title: 'Résidence', dataIndex: ['Residence', 'nom'] },
    {
      title: 'Statut', width: 120,
      render: (_, s) => (blockedMoveInStudentIds.has(String(s?.id))
        ? <span className="tag tag-red">Bloquant</span> : '-'),
    },
  ], [blockedMoveInStudentIds]);

  const onConfirmPickStudent = () => {
    if (!selectedStudentId) return;

    if (!editing && inspectionType === 'MOVE_IN' && blockedMoveInStudentIds.has(String(selectedStudentId))) {
      notification.warning({
        message: "MOVE-IN bloqué",
        description: "Cet étudiant a déjà un MOVE-IN signé. Vous devez finaliser le MOVE-OUT avant de créer un nouveau MOVE-IN.",
        placement: 'topRight',
      });
      return;
    }

    form.setFieldsValue({ student_id: selectedStudentId });
    onStudentChange(selectedStudentId);
    setPickStudentOpen(false);
  };

  const onEdit = useCallback((record) => {
    setEditing(record);
    setSelectedStudentId(record?.student_id || null);
    setStudentSearch('');
    const itemsByLabel = Object.fromEntries(
      (Array.isArray(record?.RoomReportItems) ? record.RoomReportItems : []).map((it) => [it?.label, it])
    );
    const items = CONDITION_ITEMS.map((label) => {
      const found = itemsByLabel[label];
      return {
        label,
        code: found?.code || null,
        comment: found?.comment || null,
      };
    });
    form.setFieldsValue({
      student_id: record.student_id,
      session: record.session,
      room_number: record.room_number,
      tenant_name: record.tenant_name,
      tenant_id: record.tenant_id,
      tenant_phone: record.tenant_phone,
      inspection_type: record.inspection_type,
      move_in_date: record.move_in_date ? dayjs(record.move_in_date) : null,
      move_out_date: record.move_out_date ? dayjs(record.move_out_date) : null,
      final_inspection_date: record.final_inspection_date ? dayjs(record.final_inspection_date) : null,
      tenant_signature: record.tenant_signature,
      supervisor_signature: record.supervisor_signature,
      commentaires: record.commentaires,
      status: record.status || (record.valide ? 'FINAL' : (record.hold ? 'HOLD' : 'DRAFT')),
      hold_reason: record.hold_reason,
      hold_cost: record.hold_cost,
      student_signed_at: record.student_signed_at,
      items,
    });
    setOpen(true);
  }, [form]);

  const onDeleteRow = useCallback(async (record) => {
    try {
      await deleteRoomReport(record.id);
      message.success('Rapport supprimé avec succès.');
      setViewing(null);
      fetchAll();
    } catch (e) {
      notification.error({ message: getApiErrorMessage(e, 'Suppression échouée.'), placement: 'topRight' });
    }
  }, [fetchAll, message, notification]);

  const createMoveOutFrom = async (record) => {
    try {
      if (!record?.id) return;
      setMoveOutTarget(record);
      moveOutForm.resetFields();
      moveOutForm.setFieldsValue({ session: '' });
      setMoveOutOpen(true);
    } catch (e) {
      notification.error({
        message: getApiErrorMessage(e, 'Création du move-out échouée.'),
        placement: 'topRight',
      });
    }
  };

  const submitCreateMoveOut = async () => {
    try {
      const v = await moveOutForm.validateFields();
      if (!moveOutTarget?.id) return;
      setActionLoading(true);
      const created = await createMoveOutRoomReport(moveOutTarget.id, { session: v.session });
      setMoveOutOpen(false);
      setMoveOutTarget(null);
      if (created) onEdit(created);
      message.success('Move-out créé.');
    } catch (e) {
      const apiMsg = e?.response?.data?.error;
      if (e?.response?.status === 409 && apiMsg) {
        notification.warning({ message: apiMsg, placement: 'topRight' });
      } else {
        notification.error({
          message: getApiErrorMessage(e, 'Création du move-out échouée.'),
          placement: 'topRight',
        });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const onSubmit = async ({ statusOverride } = {}) => {
    try {
      const v = await form.validateFields();
      const items = v.items || [];
      const equipementsText = (items || [])
        .map((it) => {
          const label = it?.label || '';
          const code = it?.code || '';
          const comment = it?.comment || '';
          const main = label ? `${label}: ${code}` : code;
          if (!main && !comment) return '';
          return comment ? `${main} - ${comment}` : main;
        })
        .filter((line) => line && line.trim().length)
        .join('\n');

      const payload = {
        student_id: v.student_id,
        session: v.session,
        equipements: equipementsText,
        commentaires: v.commentaires,
        status: statusOverride || v.status,
        hold_reason: v.hold_reason,
        hold_cost: v.hold_cost,
        room_number: v.room_number || null,
        tenant_name: v.tenant_name || null,
        tenant_id: v.tenant_id || null,
        tenant_phone: v.tenant_phone || null,
        inspection_type: v.inspection_type || null,
        move_in_date: v.move_in_date ? v.move_in_date.format('YYYY-MM-DD') : null,
        move_out_date: v.move_out_date ? v.move_out_date.format('YYYY-MM-DD') : null,
        final_inspection_date: v.final_inspection_date ? v.final_inspection_date.format('YYYY-MM-DD') : null,
        tenant_signature: v.tenant_signature || null,
        supervisor_signature: v.supervisor_signature || null,
        items: (items || []).map((it) => ({
          label: it.label,
          code: it.code || null,
          comment: it.comment || null,
        })),
      };
      if (editing) {
        await updateRoomReport(editing.id, payload);
        message.success('Rapport modifié avec succès.');
      } else {
        await createRoomReport(payload);
        message.success('Rapport créé avec succès.');
      }
      setOpen(false);
      await fetchAll();
    } catch (e) {
      if (e?.response || e?.message) {
        const apiMsg = e?.response?.data?.error;
        if (e?.response?.status === 409 && apiMsg) {
          notification.warning({ message: apiMsg, placement: 'topRight' });
        } else {
          notification.error({
            message: getApiErrorMessage(e, 'Enregistrement du rapport échoué.'),
            placement: 'topRight',
          });
        }
      }
    }
  };

  const openHoldModal = (record) => {
    setHoldTarget(record);
    holdForm.resetFields();
    holdForm.setFieldsValue({ reason: record?.hold_reason || null, cost: record?.hold_cost ?? null });
    setHoldOpen(true);
  };

  const submitHold = async () => {
    try {
      const v = await holdForm.validateFields();
      if (!holdTarget?.id) return;
      setActionLoading(true);
      await applyHoldRoomReport(holdTarget.id, { reason: v.reason, cost: v.cost });
      message.success('HOLD appliqué.');
      setHoldOpen(false);
      setHoldTarget(null);
      await fetchAll();
    } catch (e) {
      if (e?.response || e?.message) {
        notification.error({ message: getApiErrorMessage(e, 'Application du HOLD échouée.'), placement: 'topRight' });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const doFinalize = async (record) => {
    try {
      if (!record?.id) return;
      setActionLoading(true);
      await finalizeRoomReport(record.id);
      message.success('Rapport finalisé.');
      await fetchAll();
    } catch (e) {
      notification.error({ message: getApiErrorMessage(e, 'Finalisation échouée.'), placement: 'topRight' });
    } finally {
      setActionLoading(false);
    }
  };

  const columns = useMemo(() => [
    { title: 'ID étudiant', dataIndex: ['Student', 'student_code'] },
    { title: 'Étudiant', render: (_, r) => `${r.Student?.User?.prenom || ''} ${r.Student?.User?.nom || ''}` },
    { title: 'Résidence', dataIndex: ['Student', 'Residence', 'nom'] },
    { title: 'Chambre', dataIndex: 'room_number' },
    { title: 'Session', dataIndex: 'session' },
    { title: 'Type', dataIndex: 'inspection_type' },
    { title: 'Statut', dataIndex: 'status', render: (v) => renderStatusTag(v) },
    { title: 'Signé (étudiant)', dataIndex: 'student_signed_at', render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-' },
    { title: 'Hold', dataIndex: 'hold', render: (v) => v ? 'Oui' : 'Non' },
    { title: 'Validé', dataIndex: 'valide', render: (v) => v ? 'Oui' : 'Non' },
    { title: 'Actions', render: (_, r) => (
      <div className="actions-cell">
        <button className="action-btn" title="Voir" onClick={() => setViewing(r)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        </button>
        <button className="action-btn" title="Modifier" onClick={() => onEdit(r)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        </button>
        <Popconfirm title="Supprimer ce rapport ?" description={`${r.session}`} okText="Supprimer" cancelText="Annuler" okType="danger" onConfirm={() => onDeleteRow(r)}>
          <button className="action-btn danger" title="Supprimer">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
          </button>
        </Popconfirm>
      </div>
    )},
  ], [onDeleteRow, onEdit]);

  const filteredRows = useMemo(() => {
    const q = String(tableSearch || '').trim().toLowerCase();
    const base = Array.isArray(rows) ? rows : [];
    if (!q) return base;
    return base.filter((r) => {
      const code = String(r?.Student?.student_code || '').toLowerCase();
      const prenom = String(r?.Student?.User?.prenom || '').toLowerCase();
      const nom = String(r?.Student?.User?.nom || '').toLowerCase();
      const email = String(r?.Student?.User?.email || '').toLowerCase();
      const residence = String(r?.Student?.Residence?.nom || '').toLowerCase();
      const chambre = String(r?.room_number || r?.Student?.chambre || '').toLowerCase();
      return code.includes(q) || prenom.includes(q) || nom.includes(q) || email.includes(q) || residence.includes(q) || chambre.includes(q);
    });
  }, [rows, tableSearch]);

  const exportExcel = () => {
    if (!rows || rows.length === 0) {
      message.warning('Aucune donnée à exporter.');
      return;
    }

    const formatDate = (d) => {
      if (!d) return '';
      const date = new Date(d);
      if (isNaN(date.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    const data = rows.map(r => ({
      'ID Étudiant': r.Student?.student_code || '',
      'Étudiant': `${r.Student?.User?.prenom || ''} ${r.Student?.User?.nom || ''}`,
      'Résidence': r.Student?.Residence?.nom || '',
      'Chambre': r.room_number || '',
      'Session': r.session || '',
      'Type': r.inspection_type || '',
      'Statut': r.status || '',
      'Signé par l\'étudiant le': formatDate(r.student_signed_at),
      'En Retenue (Hold)': r.hold ? 'Oui' : 'Non',
      'Raison du Hold': r.hold_reason || '',
      'Coût estimé': r.hold_cost ?? '',
      'Équipements': r.equipements || '',
      'Commentaires': r.commentaires || '',
      'Validé': r.valide ? 'Oui' : 'Non',
      'Créé le': formatDate(r.createdAt),
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();

    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, // ID
      { wch: 25 }, // Etudiant
      { wch: 20 }, // Residence
      { wch: 12 }, // Chambre
      { wch: 15 }, // Session
      { wch: 12 }, // Type
      { wch: 12 }, // Statut
      { wch: 22 }, // Signé le
      { wch: 18 }, // Hold
      { wch: 30 }, // Raison Hold
      { wch: 15 }, // Cout
      { wch: 40 }, // Equipements
      { wch: 40 }, // Commentaires
      { wch: 10 }, // Valide
      { wch: 18 }  // Créé le
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Rapports');
    XLSX.writeFile(wb, `rapports-chambre-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.xlsx`);
  };

  const exportSinglePdf = (report) => {
    if (!report) return;
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Inventory and Room Condition Report', 14, 20);

    const formatDate = (d) => {
      if (!d) return '';
      const date = new Date(d);
      if (isNaN(date.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    autoTable(doc, {
      startY: 25,
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185] },
      body: [
        ['Student', `${report.Student?.User?.prenom || ''} ${report.Student?.User?.nom || ''}`, 'Student ID', report.Student?.student_code || ''],
        ['Residence', report.Student?.Residence?.nom || '', 'Room', report.room_number || ''],
        ['Session', report.session || '', 'Type', report.inspection_type || ''],
        ['Status', report.status || '', 'Student Signed At', formatDate(report.student_signed_at)],
        ['Move-in Date', formatDate(report.move_in_date), 'Move-out Date', formatDate(report.move_out_date)],
        ['Final Inspection Date', formatDate(report.final_inspection_date), 'Hold', report.hold ? 'Yes' : 'No'],
        ['Hold Reason', report.hold_reason || '', 'Hold Cost', report.hold_cost ?? ''],
        ['Tenant Name', report.tenant_name || '', 'Tenant ID', report.tenant_id || ''],
        ['Tenant Phone', report.tenant_phone || '', '', ''],
      ],
      styles: { fontSize: 10 },
      columnStyles: {
        0: { fontStyle: 'bold', fillColor: [240, 240, 240] },
        2: { fontStyle: 'bold', fillColor: [240, 240, 240] }
      }
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [['Condition Item', 'Status Code', 'Comments']],
      body: getConditionRows(report).map(row => [row.label || '', row.code || '', row.comment || '']),
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] },
      styles: { fontSize: 10 }
    });

    let finalY = doc.lastAutoTable.finalY + 10;
    
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text('Comments:', 14, finalY);
    finalY += 6;
    doc.setFont(undefined, 'normal');
    const comments = String(report.commentaires || 'None');
    const wrapped = doc.splitTextToSize(comments, 180);
    doc.text(wrapped, 14, finalY);
    finalY += (wrapped.length * 6) + 10;
    
    autoTable(doc, {
      startY: finalY,
      theme: 'plain',
      body: [
        ['Tenant\'s signature:', report.tenant_signature || '', 'Supervisor\'s signature:', report.supervisor_signature || ''],
        ['Validated:', report.valide ? 'Yes' : 'No', '', '']
      ],
      styles: { fontSize: 10 },
      columnStyles: {
        0: { fontStyle: 'bold' },
        2: { fontStyle: 'bold' }
      }
    });

    const filename = `room-report-${report.id || 'detail'}-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.pdf`;
    doc.save(filename);
  };

  return (
    <div className="page">
      <div className="section-label">Rapports de chambre</div>
      <div className="toolbar">
        <Select
          style={{ width: 160 }}
          value={filters.valide}
          onChange={(v) => setFilters((f) => ({ ...f, valide: v }))}
          options={[{ value: '', label: 'Tous' }, { value: 'true', label: 'Validés' }, { value: 'false', label: 'Non validés' }]}
        />
        <button className="btn-outline" onClick={exportExcel}>Export Excel</button>
        <button className="btn-primary" onClick={onAdd}>+ Nouveau</button>
      </div>

      <input
        className="search-input"
        placeholder="Rechercher un étudiant (ID, nom, email, résidence, chambre)"
        value={tableSearch}
        onChange={(e) => setTableSearch(e.target.value)}
      />

      <div className="table-card"><div className="table-scroll">
      <Table className="supervisor-reports-main-table" rowKey="id" loading={loading} columns={columns} dataSource={filteredRows} pagination={{ hideOnSinglePage: true, pageSize: 10 }} />
      </div></div>

      <Modal
        title="Détail du rapport"
        open={!!viewing}
        onCancel={() => setViewing(null)}
        width={980}
        centered
        destroyOnClose
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn-outline" onClick={() => viewing && exportSinglePdf(viewing)}>Exporter en PDF</button>
            <button className="btn-outline" onClick={() => setViewing(null)}>Fermer</button>
          </div>
        }
      >
        {viewing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="Créé le">{viewing.createdAt ? dayjs(viewing.createdAt).format('DD/MM/YYYY HH:mm:ss') : '-'}</Descriptions.Item>
              <Descriptions.Item label="Mis à jour le">{viewing.updatedAt ? dayjs(viewing.updatedAt).format('DD/MM/YYYY HH:mm:ss') : '-'}</Descriptions.Item>

              <Descriptions.Item label="ID étudiant">{viewing.Student?.student_code || '-'}</Descriptions.Item>
              <Descriptions.Item label="Étudiant">{`${viewing.Student?.User?.prenom || ''} ${viewing.Student?.User?.nom || ''}`.trim() || '-'}</Descriptions.Item>

              <Descriptions.Item label="Email">{viewing.Student?.User?.email || '-'}</Descriptions.Item>
              <Descriptions.Item label="Résidence">{viewing.Student?.Residence?.nom || '-'}</Descriptions.Item>

              <Descriptions.Item label="Chambre">{viewing.room_number || '-'}</Descriptions.Item>
              <Descriptions.Item label="Session">{viewing.session || '-'}</Descriptions.Item>

              <Descriptions.Item label="Type">{viewing.inspection_type || '-'}</Descriptions.Item>
              <Descriptions.Item label="Statut">{renderStatusTag(viewing.status)}</Descriptions.Item>

              <Descriptions.Item label="Signé (étudiant)">{viewing.student_signed_at ? dayjs(viewing.student_signed_at).format('DD/MM/YYYY HH:mm') : '-'}</Descriptions.Item>
              <Descriptions.Item label="HOLD">{(viewing.status === 'HOLD' || viewing.hold) ? 'Oui' : 'Non'}</Descriptions.Item>

              {(viewing.status === 'HOLD' || viewing.hold) && (
                <>
                  <Descriptions.Item label="Raison HOLD" span={2}>{viewing.hold_reason || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Coût HOLD" span={2}>{viewing.hold_cost ?? '-'}</Descriptions.Item>
                </>
              )}
            </Descriptions>

            <hr style={{ margin: '2px 0', border: 'none', borderTop: '0.5px solid rgba(0,0,0,0.08)' }} />

            <Descriptions size="small" bordered column={2}>
              {viewing.inspection_type === 'MOVE_IN' && (
                <>
                  <Descriptions.Item label="Move-in date">{viewing.move_in_date || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Move-out report">
                    {(() => {
                      const child = Array.isArray(viewing.Children) ? viewing.Children.find((c) => c?.inspection_type === 'MOVE_OUT') : null;
                      if (!child) return '-';
                      return `${child.status || 'DRAFT'} (id: ${child.id})`;
                    })()}
                  </Descriptions.Item>
                </>
              )}
              {viewing.inspection_type === 'MOVE_OUT' && (
                <>
                  <Descriptions.Item label="Move-out date">{viewing.move_out_date || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Final inspection date">{viewing.final_inspection_date || '-'}</Descriptions.Item>
                </>
              )}
            </Descriptions>

            <hr style={{ margin: '2px 0', border: 'none', borderTop: '0.5px solid rgba(0,0,0,0.08)' }} />

            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="Tenant name">{viewing.tenant_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="Tenant ID">{viewing.tenant_id || '-'}</Descriptions.Item>
              <Descriptions.Item label="Tenant phone" span={2}>{viewing.tenant_phone || '-'}</Descriptions.Item>
            </Descriptions>

            <hr style={{ margin: '2px 0', border: 'none', borderTop: '0.5px solid rgba(0,0,0,0.08)' }} />

            <strong style={{ fontSize: 12 }}>Room condition</strong>
            <Table
              rowKey="raw"
              size="small"
              bordered
              pagination={{ hideOnSinglePage: true, pageSize: 5, size: 'small' }}
              dataSource={getConditionRows(viewing)}
              columns={[
                { title: 'Élément', dataIndex: 'label' },
                { title: 'Code', dataIndex: 'code', width: 140 },
                { title: 'Commentaire', dataIndex: 'comment' },
              ]}
              locale={{ emptyText: 'Aucune donnée' }}
            />

            <hr style={{ margin: '2px 0', border: 'none', borderTop: '0.5px solid rgba(0,0,0,0.08)' }} />

            <div>
              <strong style={{ fontSize: 12 }}>Commentaires</strong>
              <p style={{ marginBottom: 0, whiteSpace: 'pre-wrap', fontSize: 13, color: '#444' }}>{viewing.commentaires || '-'}</p>
            </div>

            <hr style={{ margin: '2px 0', border: 'none', borderTop: '0.5px solid rgba(0,0,0,0.08)' }} />

            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="Tenant's signature">{viewing.tenant_signature || '-'}</Descriptions.Item>
              <Descriptions.Item label="Supervisor's signature">{viewing.supervisor_signature || '-'}</Descriptions.Item>
              <Descriptions.Item label="Validé" span={2}>{viewing.valide ? 'Oui' : 'Non'}</Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      <Modal
        title="Appliquer HOLD"
        open={holdOpen}
        onCancel={() => { setHoldOpen(false); setHoldTarget(null); }}
        onOk={submitHold}
        okText="Appliquer"
        confirmLoading={actionLoading}
      >
        <Form form={holdForm} layout="vertical">
          <Form.Item name="reason" label="Raison" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="cost" label="Coût estimé">
            <InputNumber style={{ width: '100%' }} min={0} step={1} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Session du Move-out"
        open={moveOutOpen}
        onCancel={() => { setMoveOutOpen(false); setMoveOutTarget(null); }}
        onOk={submitCreateMoveOut}
        okText="Créer"
        confirmLoading={actionLoading}
        centered
        zIndex={2500}
      >
        <Form form={moveOutForm} layout="vertical">
          <Form.Item name="session" label="Session (Move-out)" rules={[{ required: true }]}>
            <Input placeholder="Ex: Move-out Spring 2026" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Choisir un étudiant"
        open={pickStudentOpen}
        onCancel={() => { setPickStudentOpen(false); setStudentSearch(''); }}
        onOk={onConfirmPickStudent}
        okText="Sélectionner"
        okButtonProps={{ disabled: !selectedStudentId }}
        width={900}
        centered
        zIndex={2000}
      >
        <Input
          placeholder="Rechercher un étudiant (ID, nom, email, résidence, chambre)"
          value={studentSearch}
          onChange={(e) => setStudentSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <Table
          rowKey="id"
          size="small"
          columns={studentColumns}
          dataSource={studentRows}
          pagination={{ hideOnSinglePage: true, pageSize: 7 }}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedStudentId ? [selectedStudentId] : [],
            onChange: (nextSelectedRowKeys) => {
              const next = nextSelectedRowKeys?.[0] ?? null;
              setSelectedStudentId(next);
            },
          }}
          onRow={(record) => ({
            onClick: () => setSelectedStudentId(record.id),
          })}
          sticky
        />
      </Modal>

      <Drawer
        title={editing ? 'Modifier rapport' : 'Nouveau rapport'}
        placement="right"
        width={980}
        onClose={() => setOpen(false)}
        open={open}
        destroyOnClose
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
        extra={
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-outline" onClick={() => setOpen(false)}>Annuler</button>
            <button className="btn-outline" disabled={isSaveDraftDisabled} onClick={() => onSubmit({ statusOverride: 'DRAFT' })}>Save Draft</button>
            <button className="btn-outline"
              onClick={() => editing && createMoveOutFrom(editing)}
              disabled={!editing || editing?.status === 'FINAL' || editing?.inspection_type !== 'MOVE_IN' || editingHasFinalMoveOut}
            >Move out</button>
            <button className="btn-outline" onClick={() => editing && openHoldModal(editing)} disabled={!editing}>Apply HOLD</button>
            <button className="btn-primary"
              onClick={() => editing && doFinalize(editing)}
              disabled={!editing || !editing?.student_signed_at}
            >Finalize</button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="student_id" rules={[{ required: true }]} hidden>
            <Input />
          </Form.Item>

          <strong style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>Étudiant</strong>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <Input
              readOnly
              value={(() => {
                const s = studentById[selectedStudentFormId];
                if (!s) return '';
                const name = `${s?.User?.prenom || ''} ${s?.User?.nom || ''}`.trim();
                const code = s?.student_code || '';
                const res = s?.Residence?.nom || '';
                return `${code} — ${name}${res ? ` — ${res}` : ''}`;
              })()}
              placeholder="Sélectionnez un étudiant"
            />
            {isSelectedStudentMoveInBlocked && !editing ? <span className="tag tag-red">MOVE-IN bloqué</span> : null}
            <button className="btn-outline" type="button" onClick={openPickStudent}>Choisir</button>
            <button className="btn-outline" type="button"
              onClick={() => { form.setFieldsValue({ student_id: null, room_number: '' }); setSelectedStudentId(null); }}
              disabled={!selectedStudentFormId}
            >Effacer</button>
          </div>

          <Form.Item name="session" label="Session" rules={[{ required: true }]}>
            <Input placeholder="Ex: Move-in / Move-out Spring 2026" />
          </Form.Item>
          <Form.Item name="room_number" label="Room number">
            <Input />
          </Form.Item>
          <Form.Item name="inspection_type" label="Type d'inspection">
            <Select
              allowClear
              disabled={!!editing}
              options={[
                { label: 'Move-in', value: 'MOVE_IN' },
                { label: 'Move-out', value: 'MOVE_OUT' },
              ]}
            />
          </Form.Item>
          {inspectionType === 'MOVE_IN' && (
            <Form.Item name="move_in_date" label="Move-in date">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          )}

          {inspectionType === 'MOVE_OUT' && (
            <>
              <Form.Item name="move_out_date" label="Move-out date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="final_inspection_date" label="Final move-out inspection date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}
          <Form.Item name="tenant_name" label="Tenant name">
            <Input />
          </Form.Item>
          <Form.Item name="tenant_id" label="Tenant ID">
            <Input />
          </Form.Item>
          <Form.Item name="tenant_phone" label="Tenant phone">
            <Input />
          </Form.Item>
          <strong style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>Room condition (codes)</strong>
          {CONDITION_ITEMS.map((label, index) => (
            <div key={label} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'baseline' }}>
              <span style={{ width: 180, fontSize: 12, flexShrink: 0 }}>{label}</span>
              <Form.Item
                name={['items', index, 'code']}
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select
                  allowClear
                  options={CONDITION_CODES}
                  placeholder="Code"
                />
              </Form.Item>
              <Form.Item
                name={['items', index, 'comment']}
                style={{ flex: 2, marginBottom: 0 }}
              >
                <Input placeholder="Commentaire" />
              </Form.Item>
              <Form.Item name={['items', index, 'label']} initialValue={label} hidden><Input type="hidden" /></Form.Item>
            </div>
          ))}
          <Form.Item name="tenant_signature" label="Tenant's signature">
            <Input />
          </Form.Item>
          <Form.Item name="supervisor_signature" label="Residence supervisor's signature">
            <Input />
          </Form.Item>
          <Form.Item name="commentaires" label="Commentaires">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="status" label="Statut" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="hold_reason" label="Hold reason" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="hold_cost" label="Hold cost" hidden>
            <Input type="hidden" />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
